import React, { useEffect, useMemo, useState } from 'react';
import { loadBadBibleTabs } from '../../../services/badBibleService';
import {
	TABLE_DEFAULT,
	bibleRef,
	bibleGatewayUrl,
	buildTabs,
} from './badBibleModel';
import './badBible.css';

/* The Bad Bible space body: a searchable browser over the tabs of Anthony's
   Bad Bible Google Sheet. Read-only; the data is refreshed by re-running
   scripts/seedBadBible.js, never edited here. */

const SEARCH_CAP = 400;

function Highlight({ text, query }) {
	if (!query) return text;
	const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
	return text.split(re).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
}

function ReadLink({ tabName, value }) {
	const ref = bibleRef(tabName, value);
	if (!ref) return null;
	return (
		<a className='bb-go' href={bibleGatewayUrl(ref)} target='_blank' rel='noopener noreferrer'>
			Read <i className='fa-sharp fa-solid fa-arrow-up-right-from-square' aria-hidden='true' />
		</a>
	);
}

function Entry({ tabName, entry, query }) {
	const [first, ...rest] = entry.cells;
	if (!first) return null;
	const lines = first.value.split('\n');
	let title = lines[0];
	let body = lines.slice(1).join('\n');
	if (title.length > 160) {
		body = first.value;
		title = '';
	}
	return (
		<li className='bb-entry'>
			{entry.eyebrow && (
				<div className='bb-eyebrow'>
					<Highlight text={entry.eyebrow} query={query} />
				</div>
			)}
			{title && (
				<h3 className='bb-entry__title'>
					<Highlight text={title} query={query} />
					<ReadLink tabName={tabName} value={first.value} />
				</h3>
			)}
			{body && (
				<div className='bb-text'>
					<Highlight text={body} query={query} />
					{!title && <ReadLink tabName={tabName} value={first.value} />}
				</div>
			)}
			{rest.map((c, i) => (
				<div key={i} className={`bb-field${c.label ? '' : ' bb-field--nolab'}`}>
					{c.label && <div className='bb-field__lab'>{c.label}</div>}
					<div className='bb-text'>
						<Highlight text={c.value} query={query} />
						<ReadLink tabName={tabName} value={c.value} />
					</div>
				</div>
			))}
		</li>
	);
}

function SheetTable({ tab }) {
	const firstFilled = tab.rows[0] ? tab.rows[0].filter((c) => c).length : 0;
	return (
		<div className='bb-tablebox'>
			<table className='bb-table'>
				<tbody>
					{tab.rows.map((r, i) => {
						const n = r.filter((c) => c).length;
						if (n === 1 && r[0] && r.length > 1) {
							return (
								<tr key={i} className='bb-table__sec'>
									<td colSpan={r.length}>{r[0]}</td>
								</tr>
							);
						}
						const head = i === 0 || (i === 1 && firstFilled <= 2);
						return (
							<tr key={i} className={head ? 'bb-table__head' : undefined}>
								{r.map((c, j) => (
									<td key={j}>{c}</td>
								))}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function BadBibleBody() {
	const [tabs, setTabs] = useState(null);
	const [error, setError] = useState(null);
	const [currentId, setCurrentId] = useState(null);
	const [draft, setDraft] = useState('');
	const [query, setQuery] = useState('');
	const [tableOverride, setTableOverride] = useState({});

	useEffect(() => {
		let live = true;
		loadBadBibleTabs()
			.then((docs) => {
				if (!live) return;
				const built = buildTabs(docs);
				setTabs(built);
				setCurrentId((id) => id || built[0]?.id || null);
			})
			.catch((e) => live && setError(e));
		return () => {
			live = false;
		};
	}, []);

	useEffect(() => {
		const t = setTimeout(() => setQuery(draft.trim()), 150);
		return () => clearTimeout(t);
	}, [draft]);

	const results = useMemo(() => {
		if (!tabs || !query) return null;
		const q = query.toLowerCase();
		let shown = 0;
		let hits = 0;
		const groups = [];
		for (const t of tabs) {
			const matches = [];
			for (const s of t.sections) for (const e of s.entries) if (e.text.includes(q)) matches.push(e);
			if (!matches.length) continue;
			hits += matches.length;
			const take = matches.slice(0, Math.max(0, SEARCH_CAP - shown));
			shown += take.length;
			groups.push({ tab: t, total: matches.length, entries: take });
		}
		return { hits, groups };
	}, [tabs, query]);

	if (error) {
		return (
			<div className='psp-shell-error' role='status'>
				<i className='fa-sharp fa-solid fa-triangle-exclamation' />
				<span>
					Couldn't load the Bad Bible tabs. Please tell Anthony right away.
					<small className='bb-errdetail'>{error.code || error.message}</small>
				</span>
			</div>
		);
	}
	if (!tabs) return <p className='bb-muted'>Loading the sheet…</p>;
	if (!tabs.length) {
		return (
			<p className='bb-muted'>
				No tabs yet. Run <code>node scripts/seedBadBible.js</code> to copy the sheet in.
			</p>
		);
	}

	const total = tabs.reduce((a, t) => a + t.count, 0);
	const tab = tabs.find((t) => t.id === currentId) || tabs[0];
	const asTable = tableOverride[tab.id] ?? TABLE_DEFAULT.has(tab.name);

	const pick = (id) => {
		setDraft('');
		setQuery('');
		setCurrentId(id);
		window.scrollTo({ top: 0 });
	};

	const random = () => {
		let items = [...document.querySelectorAll('.bb-main .bb-entry')];
		if (!items.length) return;
		const el = items[Math.floor(Math.random() * items.length)];
		el.classList.remove('bb-flash');
		void el.offsetWidth;
		el.classList.add('bb-flash');
		el.scrollIntoView({ behavior: 'smooth', block: 'center' });
	};

	return (
		<div className='bb'>
			<div className='bb-bar' role='search'>
				<input
					id='bb-search'
					type='search'
					className='bb-search'
					placeholder='Search every tab (slavery, Isaiah 7, Judas)'
					aria-label='Search every tab'
					autoComplete='off'
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
				/>
				<button type='button' className='btn-cyv-ghost bb-btn' onClick={random}>
					<i className='fa-sharp fa-solid fa-shuffle' aria-hidden='true' /> Random
				</button>
				<span className='bb-stats'>
					{tabs.length} tabs · {total.toLocaleString()} entries
				</span>
			</div>

			<div className='bb-layout'>
				<nav className='bb-index' aria-label='Sheet tabs'>
					<ol>
						{tabs.map((t) => (
							<li key={t.id}>
								<button
									type='button'
									aria-current={!query && t.id === tab.id ? 'true' : undefined}
									onClick={() => pick(t.id)}>
									<span>{t.name}</span>
									<span className='bb-n'>{t.count}</span>
								</button>
							</li>
						))}
					</ol>
				</nav>

				<div className='bb-main'>
					{results ? (
						<>
							<div className='bb-head'>
								<h2>“{query}”</h2>
								<div className='bb-meta'>
									{results.hits} match{results.hits === 1 ? '' : 'es'} across all tabs
								</div>
							</div>
							{results.groups.length ? (
								results.groups.map((g) => (
									<section key={g.tab.id}>
										<div className='bb-sec'>
											<button type='button' className='bb-seclink' onClick={() => pick(g.tab.id)}>
												{g.tab.name}
											</button>
											<span>{g.total}</span>
										</div>
										<ul className='bb-entries'>
											{g.entries.map((e, i) => (
												<Entry key={i} tabName={g.tab.name} entry={e} query={query} />
											))}
										</ul>
									</section>
								))
							) : (
								<p className='bb-muted'>
									Nothing matches. Try a book name, a keyword like “prophecy”, or a shorter phrase.
								</p>
							)}
						</>
					) : (
						<>
							<div className='bb-head'>
								<div>
									<div className='bb-meta'>{tab.count} entries</div>
									<h2>{tab.name}</h2>
								</div>
								<div className='bb-view' role='group' aria-label='View'>
									<button
										type='button'
										aria-pressed={!asTable}
										onClick={() => setTableOverride((o) => ({ ...o, [tab.id]: false }))}>
										Entries
									</button>
									<button
										type='button'
										aria-pressed={asTable}
										onClick={() => setTableOverride((o) => ({ ...o, [tab.id]: true }))}>
										Sheet
									</button>
								</div>
							</div>
							{!tab.count ? (
								<p className='bb-muted'>This tab is still empty in the sheet.</p>
							) : asTable ? (
								<SheetTable tab={tab} />
							) : (
								tab.sections.map((s, si) => (
									<section key={si}>
										{s.title && <div className='bb-sec'>{s.title}</div>}
										<ul className='bb-entries'>
											{s.entries.map((e, i) => (
												<Entry key={i} tabName={tab.name} entry={e} query='' />
											))}
										</ul>
									</section>
								))
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default BadBibleBody;
